import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { feature } from 'topojson-client';
import worldTopo from 'world-atlas/countries-110m.json';
import { Box, Typography, LinearProgress } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Select } from 'Components/ui';

// Convert TopoJSON → GeoJSON once
const geoData = feature(worldTopo, worldTopo.objects.countries);

// AWS region → [longitude, latitude]
const REGION_COORDS = {
  'us-east-1':      [-77.5, 38.9],
  'us-east-2':      [-82.9, 40.4],
  'us-west-1':      [-122.4, 37.8],
  'us-west-2':      [-122.7, 45.5],
  'ca-central-1':   [-75.7, 45.4],
  'eu-west-1':      [-6.3, 53.3],
  'eu-west-2':      [-0.1, 51.5],
  'eu-west-3':      [2.4, 48.9],
  'eu-central-1':   [8.7, 50.1],
  'eu-north-1':     [18.1, 59.3],
  'eu-south-1':     [12.5, 41.9],
  'ap-southeast-1': [103.8, 1.4],
  'ap-southeast-2': [151.2, -33.9],
  'ap-northeast-1': [139.7, 35.7],
  'ap-northeast-2': [126.9, 37.6],
  'ap-northeast-3': [135.5, 34.7],
  'ap-south-1':     [72.9, 19.1],
  'ap-east-1':      [114.2, 22.3],
  'sa-east-1':      [-46.6, -23.6],
  'me-south-1':     [50.6, 26.1],
  'af-south-1':     [18.4, -33.9],
};

const AWS_REGION_LABEL = {
  'us-east-1': 'AWS - US East 1 (N. Virginia)',
  'us-east-2': 'AWS - US East 2 (Ohio)',
  'us-west-1': 'AWS - US West 1 (N. California)',
  'us-west-2': 'AWS - US West 2 (Oregon)',
  'ca-central-1': 'AWS - CA Central 1 (Canada)',
  'eu-west-1': 'AWS - EU West 1 (Ireland)',
  'eu-west-2': 'AWS - EU West 2 (London)',
  'eu-west-3': 'AWS - EU West 3 (Paris)',
  'eu-central-1': 'AWS - EU Central 1 (Frankfurt)',
  'eu-north-1': 'AWS - EU North 1 (Stockholm)',
  'eu-south-1': 'AWS - EU South 1 (Milan)',
  'ap-southeast-1': 'AWS - AP Southeast 1 (Singapore)',
  'ap-southeast-2': 'AWS - AP Southeast 2 (Sydney)',
  'ap-northeast-1': 'AWS - AP Northeast 1 (Tokyo)',
  'ap-northeast-2': 'AWS - AP Northeast 2 (Seoul)',
  'ap-northeast-3': 'AWS - AP Northeast 3 (Osaka)',
  'ap-south-1': 'AWS - AP South 1 (Mumbai)',
  'ap-east-1': 'AWS - AP East 1 (Hong Kong)',
  'sa-east-1': 'AWS - SA East 1 (São Paulo)',
  'me-south-1': 'AWS - ME South 1 (Bahrain)',
  'af-south-1': 'AWS - AF South 1 (Cape Town)',
};

// Theme matching the dashboard (light)
const T = {
  bg: 'var(--vo-white)',
  surface: 'var(--vo-mist)',
  border: 'var(--vo-line)',
  text: 'var(--vo-ink)',
  muted: 'var(--vo-slate)',
  dim: 'var(--vo-steel)',
  fail: 'var(--vo-danger)',
  pass: 'var(--vo-success)',
  dot: 'var(--vo-danger)',
  dotSelected: 'var(--color-brand)',
};

export function ThreatMap({ regions = [], isLoading }) {
  const { t } = useTranslation(['scanner']);
  const [selectedRegion, setSelectedRegion] = useState('__all__');

  // Only show regions we have coordinates for
  const mappedRegions = useMemo(
    () => regions.filter(r => REGION_COORDS[r.region]),
    [regions]
  );

  const selectedData = useMemo(() => {
    if (selectedRegion === '__all__') return null;
    return regions.find(r => r.region === selectedRegion) ?? null;
  }, [regions, selectedRegion]);

  // Summary totals for "All Regions"
  const totals = useMemo(() => ({
    fail: regions.reduce((s, r) => s + r.fail, 0),
    pass: regions.reduce((s, r) => s + r.pass, 0),
    total: regions.reduce((s, r) => s + r.total, 0),
  }), [regions]);

  const displayData = selectedData ?? totals;
  const displayLabel = selectedRegion === '__all__'
    ? 'All Regions'
    : (AWS_REGION_LABEL[selectedRegion] ?? selectedRegion);

  const failPct  = displayData.total > 0 ? Math.round((displayData.fail / displayData.total) * 100) : 0;
  const passPct  = displayData.total > 0 ? Math.round((displayData.pass / displayData.total) * 100) : 0;

  return (
    <Box sx={{
      backgroundColor: T.bg,
      display: 'flex',
      flexDirection: 'row',
      minHeight: 240,
    }}>
      {/* ── Left: map ──────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
        {/* Region selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2.5, pt: 2, pb: 1 }}>
          <Select
            name="threatMapRegion"
            value={selectedRegion}
            onChange={setSelectedRegion}
            fullWidth={false}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
              '& .MuiSvgIcon-root': { color: T.muted },
              // height/fontSize live here (not top-level) because Select's
              // own CLOSED_SX explicitly sets both on this exact slot —
              // a top-level override only reaches the outer root, which
              // loses to the child's own explicit value.
              '& .MuiSelect-select': { height: '32px', py: '4px', pr: '28px !important', color: T.text, fontSize: '0.78rem' },
              backgroundColor: 'var(--vo-white)',
              borderRadius: 1.5,
            }}
            options={[
              { value: '__all__', label: t('scanner:dashboard.allRegions', 'All Regions') },
              ...mappedRegions.map(r => ({ value: r.region, label: AWS_REGION_LABEL[r.region] ?? r.region })),
            ]}
          />
        </Box>

        {/* Map */}
        <Box sx={{ px: 1, pb: 1 }}>
          <ComposableMap
            width={800}
            height={340}
            projection="geoNaturalEarth1"
            projectionConfig={{ scale: 130, center: [10, 10] }}
            style={{ width: '100%', height: 'auto' }}
          >
            <ZoomableGroup zoom={1} minZoom={1} maxZoom={1}>
              <Geographies geography={geoData}>
                {({ geographies }) =>
                  geographies.map(geo => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      style={{
                        default: { fill: 'var(--vo-line)', stroke: 'var(--vo-white)', strokeWidth: 0.5, outline: 'none' },
                        hover:   { fill: 'var(--vo-line)', stroke: 'var(--vo-white)', strokeWidth: 0.5, outline: 'none' },
                        pressed: { fill: 'var(--vo-line)', stroke: 'var(--vo-white)', strokeWidth: 0.5, outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>

              {mappedRegions.map(r => {
                const coords = REGION_COORDS[r.region];
                const isSelected = selectedRegion === r.region;
                return (
                  <Marker
                    key={r.region}
                    coordinates={coords}
                    onClick={() => setSelectedRegion(isSelected ? '__all__' : r.region)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Pulse ring */}
                    <circle
                      r={isSelected ? 10 : 7}
                      fill={isSelected ? T.dotSelected : T.dot}
                      fillOpacity={0.18}
                      stroke="none"
                    />
                    {/* Core dot */}
                    <circle
                      r={isSelected ? 5 : 4}
                      fill={isSelected ? T.dotSelected : T.dot}
                      stroke={isSelected ? 'var(--vo-cyan)' : T.dot}
                      strokeWidth={1}
                    />
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>
        </Box>

        {/* Location count badge */}
        {!isLoading && mappedRegions.length > 0 && (
          <Box sx={{
            position: 'absolute', bottom: 16, left: 20,
            backgroundColor: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 5, px: 1.5, py: 0.5,
            display: 'flex', alignItems: 'center', gap: 0.5,
          }}>
            <LocationOnIcon sx={{ fontSize: 13, color: T.muted }} />
            <Typography sx={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>
              {mappedRegions.length} {mappedRegions.length === 1 ? 'Location' : 'Locations'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Right: region detail panel ─────────────────────────────────── */}
      <Box sx={{
        width: 220,
        flexShrink: 0,
        borderLeft: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: 2.5,
        py: 3,
        background: 'var(--vo-mist)',
      }}>
        {isLoading || regions.length === 0 ? (
          <Typography sx={{ color: T.muted, fontSize: '0.8rem', textAlign: 'center' }}>
            {isLoading ? 'Loading...' : 'No data'}
          </Typography>
        ) : (
          <>
            {/* Region title */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 14, color: T.muted, mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ color: T.text, fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.3 }}>
                {displayLabel}
              </Typography>
            </Box>
            <Typography sx={{ color: T.muted, fontSize: '0.72rem', mb: 2.5 }}>
              {displayData.total.toLocaleString()} {t('scanner:dashboard.totalFindings', 'Total Findings')}
            </Typography>

            {/* Fail bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: T.muted, fontSize: '0.75rem' }}>{t('scanner:dashboard.fail', 'Fail')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ color: T.text, fontSize: '0.75rem', fontWeight: 600 }}>{failPct}%</Typography>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: T.fail, flexShrink: 0 }} />
                  <Typography sx={{ color: T.fail, fontSize: '0.75rem', fontWeight: 700 }}>{displayData.fail.toLocaleString()}</Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={failPct}
                sx={{
                  height: 8, borderRadius: 4,
                  backgroundColor: 'var(--vo-line)',
                  '& .MuiLinearProgress-bar': { backgroundColor: T.fail, borderRadius: 4 },
                }}
              />
            </Box>

            {/* Pass bar */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: T.muted, fontSize: '0.75rem' }}>{t('scanner:dashboard.pass', 'Pass')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ color: T.text, fontSize: '0.75rem', fontWeight: 600 }}>{passPct}%</Typography>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: T.pass, flexShrink: 0 }} />
                  <Typography sx={{ color: T.pass, fontSize: '0.75rem', fontWeight: 700 }}>{displayData.pass.toLocaleString()}</Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={passPct}
                sx={{
                  height: 8, borderRadius: 4,
                  backgroundColor: 'var(--vo-line)',
                  '& .MuiLinearProgress-bar': { backgroundColor: T.pass, borderRadius: 4 },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
